import { apiService, resolveLoginUser } from "../services/api";

export type CompanyBankAccount = {
  id: string;
  account_title: string;
  iban?: string;
  account_number?: string;
  bic_swift?: string;
  bank?: string;
  branch_code?: string;
  branch_name?: string;
};

export type CompanyBankAccountPayload = Omit<CompanyBankAccount, "id"> & {
  branch_id?: string;
  transaction_branch_id?: string;
};

const BASE_PATH = "/pabws/api/company_bank_accounts";

type ListResponse = {
  success?: boolean;
  data?: CompanyBankAccount[];
  message?: string;
};

type ItemResponse = {
  success?: boolean;
  data?: CompanyBankAccount | null;
  message?: string;
};

type DeleteResponse = {
  success?: boolean;
  message?: string;
};

function resolveLoginBranchId(): string {
  const loginUser = resolveLoginUser() as Record<string, unknown> | undefined;
  if (!loginUser || typeof loginUser !== "object") return "";
  const candidates = [
    loginUser.branch_id,
    loginUser.branchId,
    loginUser.transaction_branch_id,
    loginUser.transactionBranchId,
  ];
  for (const value of candidates) {
    const id = String(value ?? "").trim();
    if (id) return id;
  }
  return "";
}

/** Ensure create payloads always carry the logged-in branch for branch-scoped lookups. */
function withLoginBranch(payload: CompanyBankAccountPayload): CompanyBankAccountPayload {
  const branchId =
    String(payload.transaction_branch_id ?? "").trim() ||
    String(payload.branch_id ?? "").trim() ||
    resolveLoginBranchId();
  if (!branchId) return payload;
  return {
    ...payload,
    branch_id: branchId,
    transaction_branch_id: branchId,
  };
}

function normalizeAccount(raw: Record<string, unknown> | null | undefined): CompanyBankAccount | null {
  if (!raw || typeof raw !== "object") return null;
  const id = raw.id != null ? String(raw.id) : "";
  if (!id) return null;
  return {
    id,
    account_title: String(raw.account_title ?? ""),
    iban: raw.iban != null ? String(raw.iban) : "",
    account_number: raw.account_number != null ? String(raw.account_number) : "",
    bic_swift: raw.bic_swift != null ? String(raw.bic_swift) : "",
    bank: raw.bank != null ? String(raw.bank) : "",
    branch_code: raw.branch_code != null ? String(raw.branch_code) : "",
    branch_name: raw.branch_name != null ? String(raw.branch_name) : "",
  };
}

export async function listCompanyBankAccounts(): Promise<CompanyBankAccount[]> {
  const response = (await apiService.get(BASE_PATH)) as ListResponse;
  if (!Array.isArray(response?.data)) {
    if (response?.success === false) {
      throw new Error(response.message || "Failed to load bank accounts.");
    }
    return [];
  }
  return response.data
    .map((row) => normalizeAccount(row as unknown as Record<string, unknown>))
    .filter((row): row is CompanyBankAccount => row != null);
}

export async function getCompanyBankAccount(id: string): Promise<CompanyBankAccount> {
  const response = (await apiService.get(`${BASE_PATH}/${encodeURIComponent(id)}`)) as ItemResponse;
  const account = normalizeAccount(response?.data as unknown as Record<string, unknown>);
  if (!account || response?.success === false) {
    throw new Error(response?.message || "Bank account not found.");
  }
  return account;
}

export async function createCompanyBankAccount(
  payload: CompanyBankAccountPayload
): Promise<CompanyBankAccount> {
  const body = withLoginBranch(payload);
  const response = (await apiService.post(BASE_PATH, body, {
    includeAuthToken: true,
    errorHandling: "throw",
  })) as ItemResponse;
  const account = normalizeAccount(response?.data as unknown as Record<string, unknown>);
  if (!account || response?.success === false) {
    throw new Error(response?.message || "Failed to create bank account.");
  }
  return account;
}

export async function updateCompanyBankAccount(
  id: string,
  payload: CompanyBankAccountPayload
): Promise<CompanyBankAccount> {
  const response = (await apiService.put(
    `${BASE_PATH}/${encodeURIComponent(id)}`,
    payload,
    {
      includeAuthToken: true,
      errorHandling: "throw",
    }
  )) as ItemResponse;
  const account = normalizeAccount(response?.data as unknown as Record<string, unknown>);
  if (!account || response?.success === false) {
    throw new Error(response?.message || "Failed to update bank account.");
  }
  return account;
}

export async function deleteCompanyBankAccount(id: string): Promise<void> {
  const response = (await apiService.delete(
    `${BASE_PATH}/${encodeURIComponent(id)}`,
    {
      includeAuthToken: true,
      errorHandling: "throw",
    }
  )) as DeleteResponse;
  if (response?.success === false) {
    throw new Error(response.message || "Failed to delete bank account.");
  }
}
